import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import Layout from '@layouts/default-layout';
import SEO from '@components/seo';
import renderModule from '@components/module';
import { defaultTheme } from '@styles/theme.styled';
import generateId from '@helpers/generate-id';
import { AnchorType } from '@components/anchor-nav';

const indexPageIdGenerator = generateId();

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export interface ConfigInterface {
  data: {
    page_name?: string;
    favicon?: {
      url?: string;
    };
    logo?: {
      alt: string;
      url: string;
    };
  };
}

export interface PageInterface {
  nodes: {
    last_publication_date: string;
    data: {
      page_title: {
        text: string;
      };
      meta_description: {
        text: string;
      };
      meta_image: {
        url: string;
        alt: string;
      };
      meta_title_tag: string;
      meta_description_tag: string;
      meta_image_tag: {
        url: string;
        alt: string;
      };
      secondary_nav: AnchorType[];
      body: [];
    };
  }[];
}

export interface MainPageInterface {
  prismicGeneralConfig: ConfigInterface;
  allPrismicPage: PageInterface;
}

const IndexPage: React.FC = () => {
  const {
    allPrismicPage,
    prismicGeneralConfig: {
      data: { favicon },
    },
  }: MainPageInterface = useStaticQuery(
    graphql`
      query MainPageQuery {
        prismicGeneralConfig {
          ...GeneralConfigFragment
        }
        allPrismicPage(filter: { uid: { eq: "home" } }) {
          nodes {
            ...PageFragment
          }
        }
      }
    `
  );

  const { page_title, meta_description, meta_image, body } = allPrismicPage.nodes[0].data;

  const pageHero = body.find(
    (item: {
      slice_type: string;
      primary?: { background_color?: keyof typeof defaultTheme.backgroundColors };
    }) => item.slice_type === 'page_hero'
  ) as { primary?: { background_color?: keyof typeof defaultTheme.backgroundColors } } | undefined;

  return (
    <Layout heroBgColor={pageHero?.primary?.background_color}>
      <SEO
        title={page_title.text}
        faviconHref={favicon?.url || ''}
        description={meta_description.text}
        image={meta_image}
      />
      <Container>
        {allPrismicPage.nodes[0].data.body.map(
          (module) =>
            module && (
              <React.Fragment key={indexPageIdGenerator.next().value}>
                {renderModule(module, allPrismicPage.nodes[0].last_publication_date)}
              </React.Fragment>
            )
        )}
      </Container>
    </Layout>
  );
};

export default IndexPage;
